import { type Inspection } from '@lib/spproject/types';
import { useFrame, useThree, type ThreeEvent } from '@react-three/fiber';
import { useAppSelector } from '@store/hooks';
import { memo, useCallback, useEffect } from 'react';
import { type OrthographicCamera, type Sprite, type SpriteMaterial } from 'three';

export interface SpriteUserData {
  poi: Inspection.PointOfInterest;
  id: string;
  name: string;
  noticeType: Inspection.NoticeType;
}

export interface PoiSpriteProps {
  sprite: Sprite;
  onPointClick: (poi: Inspection.PointOfInterest) => void;
  onHover: (id: string | null) => void;
  isHovered: boolean;
}

export const PoiSprite = memo(({ sprite, onPointClick, onHover, isHovered }: PoiSpriteProps) => {
  const userData = sprite.userData as SpriteUserData;
  const { camera } = useThree();
  const { isPerspective } = useAppSelector((state) => state.viewer);

  useFrame(({ size }) => {
    if (!isHovered) {
      let targetScale: number;

      if (!isPerspective && camera.type === 'OrthographicCamera') {
        const orthoCamera = camera as OrthographicCamera;

        const frustumHeight = (orthoCamera.top - orthoCamera.bottom) / orthoCamera.zoom;
        const targetPixelSize = 44;
        const worldUnitsPerPixel = frustumHeight / size.height;
        targetScale = (targetPixelSize * worldUnitsPerPixel * 256) / 256;
      } else {
        const targetPixelSize = 44;
        const normalizedScale = targetPixelSize / size.height;
        targetScale = normalizedScale;
      }

      sprite.scale.set(targetScale, targetScale, targetScale);
      sprite.renderOrder = 0;
      (sprite.material as SpriteMaterial).depthTest = false;
    }
  });

  useFrame(({ size }) => {
    if (!isHovered) return;

    let targetScale: number;

    if (!isPerspective && camera.type === 'OrthographicCamera') {
      const orthoCamera = camera as OrthographicCamera;
      const frustumHeight = (orthoCamera.top - orthoCamera.bottom) / orthoCamera.zoom;
      const targetPixelSize = 50;
      const worldUnitsPerPixel = frustumHeight / size.height;
      targetScale = (targetPixelSize * worldUnitsPerPixel * 256) / 256;
    } else {
      const targetPixelSize = 50;
      const normalizedScale = targetPixelSize / size.height;
      targetScale = normalizedScale;
    }

    const currentScale = sprite.scale.x;
    const smoothScale = currentScale + (targetScale - currentScale) * 0.2;
    sprite.scale.set(smoothScale, smoothScale, smoothScale);

    sprite.renderOrder = 999;
    (sprite.material as SpriteMaterial).depthTest = false;
  });

  useEffect(() => {
    const baseRaycast = sprite.raycast.bind(sprite);

    sprite.raycast = (raycaster, intersects) => {
      const before = intersects.length;
      baseRaycast(raycaster, intersects);

      if (isHovered) {
        for (let i = before; i < intersects.length; i++) {
          if (intersects[i].object === sprite) {
            intersects[i].distance = 0;
          }
        }
      }
    };

    return () => {
      sprite.raycast = baseRaycast;
    };
  }, [sprite, isHovered]);

  const handleClick = useCallback(
    (e: ThreeEvent<MouseEvent>) => {
      e.stopPropagation();
      onPointClick(userData.poi);
    },
    [userData.poi, onPointClick]
  );

  const handlePointerOver = useCallback(
    (e: ThreeEvent<PointerEvent>) => {
      e.stopPropagation();
      onHover(userData.id);
      document.body.style.cursor = 'pointer';
    },
    [userData.id, onHover]
  );

  const handlePointerOut = useCallback(
    (e: ThreeEvent<PointerEvent>) => {
      e.stopPropagation();
      onHover(null);
      document.body.style.cursor = 'default';
    },
    [onHover]
  );

  return (
    <primitive
      object={sprite}
      onClick={handleClick}
      onPointerOver={handlePointerOver}
      onPointerOut={handlePointerOut}
    />
  );
});